import React, { useState, useEffect } from "react";
import { RefreshCw, Search, Monitor, MapPin, History, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyConfig } from "@/hooks/useCompanyConfig";
import MainLayout from "@/components/layouts/MainLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import DashboardStats from "@/components/dashboard/DashboardStats";
import DashboardScreens from "@/components/dashboard/DashboardScreens";
import DashboardActiveAds from "@/components/dashboard/DashboardActiveAds";
import DashboardSellerDashboard from "@/components/dashboard/DashboardSellerDashboard";
import MaintenanceSummary from "@/components/dashboard/MaintenanceSummary";
import { getPendingMaintenanceReports } from "@/services/maintenanceService";
import { MaintenanceReport } from "@/models/maintenance";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import MaintenanceReportForm from "@/components/maintenance/MaintenanceReportForm";
import { Badge } from "@/components/ui/badge";
import NotificationCenter from "@/components/ui/NotificationCenter";

const Dashboard = () => {
  const { screens, ads, sales, currentMonthSales, isLoading, refreshData } = useData();
  const { isAdmin, isSeller, isMantenimiento, user } = useAuth();
  const { data: companyConfig } = useCompanyConfig();
  const navigate = useNavigate();

  const handleRetryLoading = () => {
    refreshData();
  };

  const totalOccupancyRate = screens.reduce((acc, screen) => acc + screen.occupancyRate, 0);
  const averageOccupancy = screens.length > 0 ? Math.round(totalOccupancyRate / screens.length) : 0;

  // Calcular ventas totales (históricas)
  const totalSales = sales.reduce((acc, sale) => acc + sale.amount, 0);
  
  // Calcular ventas del mes actual
  const totalCurrentMonthSales = currentMonthSales.reduce((acc, sale) => acc + sale.amount, 0);

  const formattedTotalSales = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(totalSales);
  
  const formattedCurrentMonthSales = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(totalCurrentMonthSales);

  console.log("Dashboard - Perfil del usuario:", {
    id: user?.id,
    email: user?.email,
    isAdmin,
    isSeller,
    isMantenimiento,
    profile: user?.profile,
    ads: ads.length,
    currentMonthSales: currentMonthSales.length
  });

  // Obtener el nombre de la empresa de la configuración
  const companyName = companyConfig?.company_name || "Mi Empresa";

  // Si el usuario es encargado de mantenimiento, mostrar un dashboard específico
  if (isMantenimiento) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedScreen, setSelectedScreen] = useState(null);
    const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
    const [pendingReports, setPendingReports] = useState<any[]>([]);

    // Cargar reportes pendientes
    useEffect(() => {
      const loadPendingReports = async () => {
        try {
          const reports = await getPendingMaintenanceReports();
          setPendingReports(reports);
        } catch (error) {
          console.error("Error al cargar reportes pendientes:", error);
          setPendingReports([]);
        }
      };
      
      loadPendingReports();
    }, []);

    // Verificar si una pantalla está en mantenimiento
    const isScreenInMaintenance = (screenId: string) => {
      return pendingReports.some(report => report.screenId === screenId);
    };

    // Filtrar pantallas por término de búsqueda
    const filteredScreens = screens.filter(screen => {
      const searchLower = searchTerm.toLowerCase();
      return (
        screen.name.toLowerCase().includes(searchLower) ||
        (screen.location && screen.location.toLowerCase().includes(searchLower))
      );
    });

    // Manejar clic en "Reportar Mantenimiento"
    const handleReportMaintenance = (screen) => {
      setSelectedScreen(screen);
      setIsReportDialogOpen(true);
    };
    
    // Manejar cuando se crea un reporte
    const handleReportCreated = () => {
      // Recargar los reportes pendientes
      const loadPendingReports = async () => {
        try {
          const reports = await getPendingMaintenanceReports();
          setPendingReports(reports);
        } catch (error) {
          console.error("Error al cargar reportes pendientes:", error);
        }
      };
      
      loadPendingReports();
    };

    // Manejar clic en "Ver historial técnico"
    const handleViewHistory = (screen) => {
      // Navegar a la página de mantenimiento con la pestaña de historial seleccionada y filtrando por la pantalla
      navigate(`/screen-maintenance?tab=completed&screenId=${screen.id}`);
    };

    return (
      <MainLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-titles tracking-tight">
                Panel de Mantenimiento
              </h1>
              <p className="text-muted-foreground font-body">
                Bienvenido al Panel de Mantenimiento, {user?.profile?.full_name || ""}
              </p>
            </div>
            <Button 
              variant="default"
              onClick={() => navigate('/screen-maintenance?tab=pending')} 
              className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-black"
            >
              Ir a Mantenimiento
            </Button>
          </div>
          
          <div className="space-y-6">
            {/* Tarjetas de estado */}
            <MaintenanceSummary />
            
            {/* Listado de Pantallas */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Listado de Pantallas</h2>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Buscar pantalla por nombre o ubicación..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border-gray-200"
                />
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                {filteredScreens.map(screen => (
                  <Card key={screen.id} className="border border-gray-200 bg-white h-full">
                    <CardContent className="p-5 flex flex-col h-full">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Monitor className="h-6 w-6" />
                          <h3 className="font-bold text-xl">{screen.name}</h3>
                        </div>
                        {isScreenInMaintenance(screen.id) ? (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300 text-base">
                            En mantenimiento
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-green-300 text-base">
                            Operativa
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-gray-500 mb-4">
                        <MapPin className="h-5 w-5" />
                        <span className="text-base">{screen.location}</span>
                      </div>
                      
                      <div className="mt-auto space-y-3">
                        <Button 
                          variant="outline" 
                          className="w-full flex items-center justify-center gap-1.5 py-2 text-base"
                          onClick={() => handleViewHistory(screen)}
                        >
                          <History className="h-5 w-5 text-blue-500" />
                          <span className="text-blue-500">Ver historial</span>
                        </Button>
                        
                        <Button
                          variant="default"
                          disabled={isScreenInMaintenance(screen.id)}
                          className="w-full flex items-center justify-center gap-1.5 py-2 text-base"
                          onClick={() => handleReportMaintenance(screen)}
                        >
                          <Wrench className="h-5 w-5" />
                          {isScreenInMaintenance(screen.id) ? 'En mantenimiento' : 'Reportar Mantenimiento'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Modal de reporte de mantenimiento */}
        {selectedScreen && (
          <MaintenanceReportForm
            open={isReportDialogOpen}
            onOpenChange={setIsReportDialogOpen}
            screen={selectedScreen}
            onReportCreated={handleReportCreated}
          />
        )}
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-titles tracking-tight">
              {isAdmin ? "Dashboard" : "Mi Dashboard"}
            </h1>
            <p className="text-muted-foreground font-body">
              {isAdmin 
                ? `Bienvenido al Panel de Control de ${companyName}` 
                : `Bienvenido a tu Panel Personal, ${user?.profile?.full_name || "Vendedor"}`}
            </p>
          </div>
          
          {/* Separamos los elementos en un contenedor con padding superior */}
          <div className="flex items-center gap-4 pt-3">
            {/* Contenedor independiente para el icono de notificación con margen */}
            {(isAdmin || isSeller) && (
              <div className="mr-2">
                <NotificationCenter />
              </div>
            )}
            
            {/* Botón de actualizar datos */}
            <Button 
              variant="outline"
              onClick={handleRetryLoading} 
              className="flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Actualizar datos
            </Button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-28 w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {isAdmin ? (
              <>
                <DashboardStats
                  screensCount={screens.length}
                  adsCount={ads.length}
                  averageOccupancy={averageOccupancy}
                />

                <DashboardScreens screens={screens} isLoading={isLoading} />
                
                <DashboardActiveAds ads={ads} isLoading={isLoading} />
              </>
            ) : (
              <DashboardSellerDashboard />
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Dashboard;
